import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import {
  clampLivePollIntervalMs,
  DEFAULT_MONITORING_LIVE_POLL_INTERVAL_MS,
  MAX_MONITORING_LIVE_POLL_INTERVAL_MS,
  MIN_MONITORING_LIVE_POLL_INTERVAL_MS,
  useGlobalMonitoringAlerts,
} from '../../contexts/GlobalMonitoringAlertContext';
import api from '../../services/api';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const INPUT_CLASS =
  'w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent';
const LABEL_CLASS = 'block text-sm font-medium text-gray-700 mb-2';
const CHIP_CLASS =
  'inline-flex items-center gap-1.5 pl-3 pr-1 py-1 rounded-full text-sm border max-w-full';
const CHIP_BTN =
  'shrink-0 p-1 rounded-full text-gray-500 hover:text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-200';

type MonitoringAlertConfigPayload = {
  primaryEmail: string;
  additionalEmails: string[];
  emailSubjectTemplate: string;
  emailBodyTemplate: string;
  cpuThreshold: number;
  memoryThreshold: number;
  storageThreshold: number;
  postgresCpuThreshold: number;
  postgresMemoryThreshold: number;
  networkThreshold: number;
  appLoadThreshold: number;
  /** Global bar + dashboard “Auto” live metrics poll (ms). */
  livePollIntervalMs: number;
  updatedAt?: string;
};

type PercentThresholdKey = keyof Pick<
  MonitoringAlertConfigPayload,
  | 'cpuThreshold'
  | 'memoryThreshold'
  | 'storageThreshold'
  | 'postgresCpuThreshold'
  | 'postgresMemoryThreshold'
  | 'networkThreshold'
>;

const emptyForm = (): MonitoringAlertConfigPayload => ({
  primaryEmail: '',
  additionalEmails: [],
  emailSubjectTemplate: 'Alert - {{metric}} exceeded threshold',
  emailBodyTemplate: `Hello {{recipientName}},

An alert has been triggered for the following metric:

🔹 Metric Name     : {{metric}}  
🔹 Current Value   : {{value}}  
🔹 Configured Limit: {{threshold}}  
🔹 Time            : {{timestamp}}  

{{thresholdDetails}}

This indicates that the system has exceeded the defined threshold and may require immediate attention.

Please review the issue and take appropriate action to prevent any potential impact on system performance or availability.

If this alert persists, kindly investigate the root cause at the earliest.

Regards,  
Monitoring System`,
  cpuThreshold: 90,
  memoryThreshold: 90,
  storageThreshold: 90,
  postgresCpuThreshold: 90,
  postgresMemoryThreshold: 90,
  networkThreshold: 90,
  appLoadThreshold: 100,
  livePollIntervalMs: DEFAULT_MONITORING_LIVE_POLL_INTERVAL_MS,
});

/** Snap to 0.5 step and clamp to [1, 100] for percent sliders. */
function clampPercentThreshold(n: number): number {
  if (!Number.isFinite(n)) return 90;
  const stepped = Math.round(n * 2) / 2;
  return Math.min(100, Math.max(1, stepped));
}

function formatThresholdPercentLabel(v: number): string {
  if (!Number.isFinite(v)) return '—';
  return Math.abs(v % 1) < 1e-6 ? String(v) : v.toFixed(1);
}

function validateClient(values: MonitoringAlertConfigPayload): string | null {
  if (!values.primaryEmail.trim() || !EMAIL_RE.test(values.primaryEmail.trim())) {
    return 'Enter a valid primary email address.';
  }
  for (const e of values.additionalEmails) {
    const t = e.trim();
    if (t && !EMAIL_RE.test(t)) return `Invalid email: ${e}`;
  }
  if (!values.emailSubjectTemplate.trim()) return 'Subject template is required.';
  if (!values.emailBodyTemplate.trim()) return 'Message body template is required.';

  const pct = (n: number, label: string) => {
    if (!Number.isFinite(n) || n < 1 || n > 100) return `${label} must be between 1 and 100.`;
    return null;
  };
  for (const [n, label] of [
    [values.postgresCpuThreshold, 'PostgreSQL Database CPU threshold'],
    [values.postgresMemoryThreshold, 'PostgreSQL Database Memory threshold'],
    [values.storageThreshold, 'PostgreSQL storage used threshold'],
    [values.cpuThreshold, 'Application CPU threshold'],
    [values.memoryThreshold, 'Application memory threshold'],
    [values.networkThreshold, 'Network threshold'],
  ] as const) {
    const err = pct(n, label);
    if (err) return err;
  }
  if (!Number.isFinite(values.appLoadThreshold) || values.appLoadThreshold <= 0) {
    return 'Application load threshold must be a positive number.';
  }
  if (
    !Number.isFinite(values.livePollIntervalMs) ||
    values.livePollIntervalMs < MIN_MONITORING_LIVE_POLL_INTERVAL_MS ||
    values.livePollIntervalMs > MAX_MONITORING_LIVE_POLL_INTERVAL_MS
  ) {
    return `Live poll interval must be between ${MIN_MONITORING_LIVE_POLL_INTERVAL_MS / 1000} and ${
      MAX_MONITORING_LIVE_POLL_INTERVAL_MS / 1000
    } seconds.`;
  }
  return null;
}

type PercentThresholdSliderProps = {
  id: string;
  label: string;
  value: number;
  onChange: (v: number) => void;
};

const PercentThresholdSlider: React.FC<PercentThresholdSliderProps> = ({ id, label, value, onChange }) => {
  const safe = clampPercentThreshold(value);
  return (
    <div className="rounded-xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-4 shadow-sm space-y-3">
      <div className="flex items-start justify-between gap-3">
        <label htmlFor={id} className="text-sm font-medium text-gray-800 leading-snug pr-2">
          {label}
        </label>
        <span
          className="shrink-0 rounded-lg bg-blue-50 border border-blue-100 px-3 py-1.5 text-sm font-semibold tabular-nums text-blue-800"
          aria-live="polite"
        >
          {formatThresholdPercentLabel(safe)}
          <span className="text-blue-600 font-normal">%</span>
        </span>
      </div>
      <div className="flex items-center gap-2 sm:gap-3">
        <span className="w-7 shrink-0 text-right text-xs font-medium text-slate-500 tabular-nums">1</span>
        <input
          id={id}
          type="range"
          min={1}
          max={100}
          step={0.5}
          value={safe}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="flex-1 h-2.5 min-w-0 cursor-pointer appearance-none rounded-full bg-slate-200 accent-blue-600
            focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2
            [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4
            [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600
            [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white
            [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110
            [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full
            [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:bg-blue-600
            [&::-moz-range-thumb]:shadow-md"
        />
        <span className="w-8 shrink-0 text-xs font-medium text-slate-500 tabular-nums">100</span>
      </div>
    </div>
  );
};

const LIVE_POLL_SLIDER_MIN_M = MIN_MONITORING_LIVE_POLL_INTERVAL_MS / 60_000;
const LIVE_POLL_SLIDER_MAX_M = MAX_MONITORING_LIVE_POLL_INTERVAL_MS / 60_000;

const LivePollIntervalSlider: React.FC<{
  valueMs: number;
  onChange: (ms: number) => void;
}> = ({ valueMs, onChange }) => {
  const ms = clampLivePollIntervalMs(valueMs);
  const minutes = ms / 60_000;
  const label = Math.abs(minutes % 1) < 1e-6 ? String(minutes) : minutes.toFixed(1);

  return (
    <div className="rounded-xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-4 shadow-sm space-y-3">
      <div className="flex items-start justify-between gap-3">
        <label htmlFor="live-poll-interval" className="text-sm font-medium text-gray-800 leading-snug pr-2">
          Live metrics poll interval
        </label>
        <span
          className="shrink-0 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-1.5 text-sm font-semibold tabular-nums text-emerald-900"
          aria-live="polite"
        >
          {label}
          <span className="text-emerald-700 font-normal"> min</span>
        </span>
      </div>
      <p className="text-xs text-slate-500 leading-relaxed">
        How often the app fetches live PostgreSQL and application metrics for the top alert bar when you are not on the
        Monitoring page. The Monitoring dashboard uses the same interval when refresh is set to{' '}
        <span className="font-medium text-slate-700">Auto</span>.
      </p>
      <div className="flex items-center gap-2 sm:gap-3">
        <span className="w-7 shrink-0 text-right text-xs font-medium text-slate-500 tabular-nums">1</span>
        <input
          id="live-poll-interval"
          type="range"
          min={LIVE_POLL_SLIDER_MIN_M}
          max={LIVE_POLL_SLIDER_MAX_M}
          step={0.5}
          value={minutes}
          onChange={(e) => onChange(Math.round(parseFloat(e.target.value) * 60_000))}
          className="flex-1 h-2.5 min-w-0 cursor-pointer appearance-none rounded-full bg-slate-200 accent-emerald-600
            focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2
            [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4
            [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-emerald-600
            [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white
            [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110
            [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full
            [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:bg-emerald-600
            [&::-moz-range-thumb]:shadow-md"
        />
        <span className="w-8 shrink-0 text-xs font-medium text-slate-500 tabular-nums">60</span>
      </div>
    </div>
  );
};

const MonitoringConfiguration: React.FC = () => {
  const { hasAnyRole } = useAuth();
  const { refreshAlertThresholds } = useGlobalMonitoringAlerts();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<MonitoringAlertConfigPayload>(emptyForm);
  const [primaryDraft, setPrimaryDraft] = useState('');
  const [additionalDraft, setAdditionalDraft] = useState('');

  const allowed = hasAnyRole(['super_admin', 'admin', 'manager']);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<MonitoringAlertConfigPayload>('/monitoring/alert-config');
      const extras = Array.isArray(data.additionalEmails)
        ? data.additionalEmails.filter((x): x is string => typeof x === 'string')
        : [];
      setForm({
        primaryEmail: data.primaryEmail ?? '',
        additionalEmails: extras,
        emailSubjectTemplate: data.emailSubjectTemplate ?? emptyForm().emailSubjectTemplate,
        emailBodyTemplate: data.emailBodyTemplate ?? emptyForm().emailBodyTemplate,
        cpuThreshold: clampPercentThreshold(Number(data.cpuThreshold)),
        memoryThreshold: clampPercentThreshold(Number(data.memoryThreshold)),
        storageThreshold: clampPercentThreshold(Number(data.storageThreshold)),
        postgresCpuThreshold: clampPercentThreshold(
          Number(data.postgresCpuThreshold ?? emptyForm().postgresCpuThreshold)
        ),
        postgresMemoryThreshold: clampPercentThreshold(
          Number(data.postgresMemoryThreshold ?? emptyForm().postgresMemoryThreshold)
        ),
        networkThreshold: clampPercentThreshold(Number(data.networkThreshold)),
        appLoadThreshold: Number(data.appLoadThreshold),
        livePollIntervalMs: clampLivePollIntervalMs(
          Number(data.livePollIntervalMs ?? DEFAULT_MONITORING_LIVE_POLL_INTERVAL_MS)
        ),
        updatedAt: data.updatedAt,
      });
    } catch {
      toast.error('Could not load monitoring configuration.');
      setForm(emptyForm());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (allowed) void load();
  }, [allowed, load]);

  const commitPrimaryEmail = () => {
    const t = primaryDraft.trim();
    if (!t) {
      toast.error('Enter an email address.');
      return;
    }
    if (!EMAIL_RE.test(t)) {
      toast.error('Enter a valid primary email address.');
      return;
    }
    setForm((f) => ({ ...f, primaryEmail: t }));
    setPrimaryDraft('');
  };

  const removePrimaryEmail = () => {
    setForm((f) => ({ ...f, primaryEmail: '' }));
  };

  const commitAdditionalEmail = () => {
    const t = additionalDraft.trim();
    if (!t) {
      toast.error('Enter an email address.');
      return;
    }
    if (!EMAIL_RE.test(t)) {
      toast.error('Enter a valid email address.');
      return;
    }
    const lower = t.toLowerCase();
    if (form.primaryEmail.trim().toLowerCase() === lower) {
      toast.error('This address is already the primary recipient.');
      return;
    }
    if (form.additionalEmails.some((e) => e.trim().toLowerCase() === lower)) {
      toast.error('This address is already in the list.');
      return;
    }
    setForm((f) => ({ ...f, additionalEmails: [...f.additionalEmails, t] }));
    setAdditionalDraft('');
  };

  const removeAdditionalEmail = (index: number) => {
    setForm((f) => ({
      ...f,
      additionalEmails: f.additionalEmails.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    const cleaned: MonitoringAlertConfigPayload = {
      ...form,
      primaryEmail: form.primaryEmail.trim(),
      additionalEmails: form.additionalEmails.map((s) => s.trim()).filter(Boolean),
      cpuThreshold: clampPercentThreshold(form.cpuThreshold),
      memoryThreshold: clampPercentThreshold(form.memoryThreshold),
      storageThreshold: clampPercentThreshold(form.storageThreshold),
      postgresCpuThreshold: clampPercentThreshold(form.postgresCpuThreshold),
      postgresMemoryThreshold: clampPercentThreshold(form.postgresMemoryThreshold),
      networkThreshold: clampPercentThreshold(form.networkThreshold),
      livePollIntervalMs: clampLivePollIntervalMs(form.livePollIntervalMs),
    };
    const err = validateClient(cleaned);
    if (err) {
      toast.error(err);
      return;
    }
    setSaving(true);
    try {
      await api.put('/monitoring/alert-config', cleaned);
      toast.success('Configuration saved.');
      await load();
      await refreshAlertThresholds();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Save failed.';
      toast.error(typeof msg === 'string' ? msg : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      await api.post('/monitoring/alert-config/reset');
      toast.success('Defaults restored.');
      await load();
      await refreshAlertThresholds();
    } catch {
      toast.error('Reset failed.');
    } finally {
      setSaving(false);
    }
  };

  if (!allowed) {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <p className="text-gray-600">Loading configuration…</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Monitoring configuration</h1>
        <p className="mt-1 text-sm text-gray-600">
          Alert thresholds, notification recipients, and the email template used when a metric
          crosses from normal to breached.
        </p>
        {form.updatedAt && (
          <p className="mt-1 text-xs text-gray-400">Last updated: {new Date(form.updatedAt).toLocaleString()}</p>
        )}
      </div>

      <section className="bg-white rounded-lg shadow p-6 space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Email settings</h2>
          <p className="mt-1 text-sm text-gray-500">
            Add addresses below; each appears as a tag. Primary must be set before you can save.
          </p>
        </div>

        <div className="space-y-3">
          <label className={LABEL_CLASS}>Primary email</label>
          {form.primaryEmail.trim() ? (
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`${CHIP_CLASS} bg-indigo-50 border-indigo-200 text-indigo-900`}
                title={form.primaryEmail.trim()}
              >
                <span className="truncate max-w-[min(100%,20rem)]">{form.primaryEmail.trim()}</span>
                <button
                  type="button"
                  className={CHIP_BTN}
                  onClick={removePrimaryEmail}
                  aria-label="Remove primary email"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </span>
            </div>
          ) : (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              No primary email yet. Enter one and click Add.
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              className={`flex-1 ${INPUT_CLASS}`}
              value={primaryDraft}
              onChange={(e) => setPrimaryDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitPrimaryEmail();
                }
              }}
              placeholder={form.primaryEmail.trim() ? 'Replace with another email…' : 'name@company.com'}
            />
            <button
              type="button"
              onClick={commitPrimaryEmail}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shrink-0"
            >
              {form.primaryEmail.trim() ? 'Replace primary' : 'Add primary'}
            </button>
          </div>
        </div>

        <div className="space-y-3">
          <label className={LABEL_CLASS}>Additional emails</label>
          {form.additionalEmails.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {form.additionalEmails.map((email, index) => (
                <span
                  key={`${email}-${index}`}
                  className={`${CHIP_CLASS} bg-gray-100 border-gray-200 text-gray-900`}
                  title={email}
                >
                  <span className="truncate max-w-[min(100%,18rem)]">{email}</span>
                  <button
                    type="button"
                    className={CHIP_BTN}
                    onClick={() => removeAdditionalEmail(index)}
                    aria-label={`Remove ${email}`}
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No additional recipients yet.</p>
          )}
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              className={`flex-1 ${INPUT_CLASS}`}
              value={additionalDraft}
              onChange={(e) => setAdditionalDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitAdditionalEmail();
                }
              }}
              placeholder="name@company.com"
            />
            <button
              type="button"
              onClick={commitAdditionalEmail}
              className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors shrink-0"
            >
              Add
            </button>
          </div>
        </div>
      </section>

      <section className="bg-white rounded-lg shadow p-6 space-y-4">
        <h2 className="text-xl font-semibold text-gray-900">Threshold settings</h2>
        <p className="text-sm text-gray-500">
          Percent-based alerts use the sliders below (1–100%, in steps of 0.5). Application load still
          uses requests per second.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {(
            [
              ['postgresCpuThreshold', 'PostgreSQL Database CPU'],
              ['postgresMemoryThreshold', 'PostgreSQL Database Memory'],
              ['storageThreshold', 'PostgreSQL Storage Used'],
              ['cpuThreshold', 'Application CPU'],
              ['memoryThreshold', 'Application Memory'],
              ['networkThreshold', 'Network load'],
            ] as const satisfies ReadonlyArray<[PercentThresholdKey, string]>
          ).map(([key, label]) => (
            <PercentThresholdSlider
              key={key}
              id={`threshold-${key}`}
              label={`${label} (%)`}
              value={form[key]}
              onChange={(v) => setForm((f) => ({ ...f, [key]: v }))}
            />
          ))}
          <div className="sm:col-span-2 rounded-xl border border-slate-200 bg-slate-50/50 p-4 shadow-sm">
            <label className={LABEL_CLASS} htmlFor="threshold-app-load">
              Application load threshold (req/sec)
            </label>
            <input
              id="threshold-app-load"
              type="number"
              min={0.0001}
              step="any"
              className={`${INPUT_CLASS} max-w-xs`}
              value={form.appLoadThreshold}
              onChange={(e) =>
                setForm((f) => ({ ...f, appLoadThreshold: parseFloat(e.target.value) || 0 }))
              }
            />
          </div>
        </div>

        <div className="sm:col-span-2">
          <LivePollIntervalSlider
            valueMs={form.livePollIntervalMs}
            onChange={(ms) => setForm((f) => ({ ...f, livePollIntervalMs: ms }))}
          />
        </div>
      </section>

      <section className="bg-white rounded-lg shadow p-6 space-y-4">
        <h2 className="text-xl font-semibold text-gray-900">Email template</h2>
        <p className="text-sm text-gray-500">
          Template variables: {'{{recipientName}}'}, {'{{recipient}}'}, {'{{recipients}}'}, {'{{recipientNames}}'},{' '}
          {'{{metric}}'}, {'{{value}}'}, {'{{threshold}}'}, {'{{timestamp}}'}, {'{{thresholdDetails}}'},{' '}
          {'{{breachDetails}}'}, {'{{breachCount}}'}
        </p>
        <div>
          <label className={LABEL_CLASS}>Subject</label>
          <input
            type="text"
            className={INPUT_CLASS}
            value={form.emailSubjectTemplate}
            onChange={(e) => setForm((f) => ({ ...f, emailSubjectTemplate: e.target.value }))}
          />
        </div>
        <div>
          <label className={LABEL_CLASS}>Message body</label>
          <textarea
            rows={22}
            className={`${INPUT_CLASS} font-mono text-sm`}
            value={form.emailBodyTemplate}
            onChange={(e) => setForm((f) => ({ ...f, emailBodyTemplate: e.target.value }))}
          />
        </div>
      </section>

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={saving}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save configuration'}
        </button>
        <button
          type="button"
          onClick={() => void handleReset()}
          disabled={saving}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          Reset to default
        </button>
      </div>
    </div>
  );
};

export default MonitoringConfiguration;
